import { useEffect, useRef, useState } from "react";
import { useCachedGLTF } from './useCachedGLTF';
import { useFrame } from '@react-three/fiber';
import * as THREE from "three";

export function Model({ url, wireframe, grid, axes, rotation }: { 
  url: string, 
  wireframe: boolean,
  grid: boolean,
  axes: boolean,
  rotation: { rotateX: number; rotateY: number; rotateZ: number }
}) {
  const scene = useCachedGLTF(url); 
  const modelRef = useRef<THREE.Group>(null);
  const { rotateX, rotateY, rotateZ } = rotation;

  useFrame(() => {
    if (modelRef.current) {
      modelRef.current.rotation.x = THREE.MathUtils.degToRad(rotateX);
      modelRef.current.rotation.y = THREE.MathUtils.degToRad(rotateY);
      modelRef.current.rotation.z = THREE.MathUtils.degToRad(rotateZ);
    }
  });

  useEffect(() => {
    const box = new THREE.Box3().setFromObject(scene);
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(size);

    scene.position.set(0, 0, 0);
    const yAxis = new THREE.Vector3(0, 1, 0);  
    scene.rotateOnAxis(yAxis, Math.PI);  
    scene.position.add(center);
  }, [scene]);

  useEffect(() => {
    //wireframe toggle 
    scene.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((mat) => {
            if ('wireframe' in mat) mat.wireframe = wireframe;
          });
        } else if ('wireframe' in mesh.material) {
          mesh.material.wireframe = wireframe;
        }
      }
    });
  }, [scene, wireframe]);

  return (
    <group 
    ref={modelRef} 
    rotation={[
      THREE.MathUtils.degToRad(rotation.rotateX),
      THREE.MathUtils.degToRad(rotation.rotateY),
      THREE.MathUtils.degToRad(rotation.rotateZ),
    ]}>
      <primitive object={scene} dispose={null} />
      { wireframe }
      { grid && <gridHelper args={[100, 100]} /> }
      { axes && <axesHelper args={[100]} /> }
    </group>
  );
}
